import type { StateCreator } from 'zustand';
import { InspectionTemplateState } from 'components/common/dataGrid/columnsFactory/types';
import { EntityName } from 'shared/domain/entityName';
import { inspectionTemplateClient } from 'shared/domain/inspectionTemplate/inspectionTemplateClient';
import { createEntityCollectionActions } from 'store/entityCollection';
import type { DataActions } from 'store/types';
import type { InspectionTemplateModel } from 'shared/domain/template/inspectionTemplateModel';
import type { InspectionStoreState } from '../../store';
import type { InspectionTemplatesSlice, InspectionTemplatesItems, InspectionTemplatesCollection } from './types';
import {
  updateDeletedInList,
  updateDeletedInSingleItem,
} from '../../helpers/updateDeletedState';

const EMPTY_TEMPLATES: InspectionTemplatesItems = {
  items: [],
  total: 0,
};

const getDefaultState = () => ({
  inspectionTemplates: EMPTY_TEMPLATES,
  inspectionTemplatesLoading: true,
  inspectionTemplatesListState: InspectionTemplateState.published,
});

type InspectionTemplatesSliceCreator = StateCreator<
  InspectionStoreState,
  [],
  [],
  InspectionTemplatesSlice
>;

export const createInspectionTemplatesSlice: InspectionTemplatesSliceCreator = (
  set,
  get
) => {
  const dataActions: DataActions<InspectionTemplateModel> = {
    setItems: (items) => get().setInspectionTemplates({ items, total: items.length }),
    setLoading: (loading) => get().setInspectionTemplatesLoading(loading),
    reset: () => get().resetInspectionTemplates(),
  };
  const inspectionTemplatesCollection: InspectionTemplatesCollection = {
    ...dataActions,
    ...createEntityCollectionActions(
      { client: inspectionTemplateClient, entityName: EntityName.inspectionTemplates },
      () => dataActions
    ),
  };

  return {
    ...getDefaultState(),
    inspectionTemplatesCollection,
    setInspectionTemplates: (inspectionTemplates) =>
      set({
        inspectionTemplates,
        inspectionTemplatesLoading: false,
      }),
    setInspectionTemplatesLoading: (inspectionTemplatesLoading) =>
      set({ inspectionTemplatesLoading }),
    setInspectionTemplatesListState: (inspectionTemplatesListState) => set({ inspectionTemplatesListState }),
    updateTemplateDeleted: (templateId, deleted) =>
      set((state) => ({
        inspectionTemplates: {
          ...state.inspectionTemplates,
          items: updateDeletedInList(state.inspectionTemplates.items, templateId, deleted),
        },
        inspectionTemplate: updateDeletedInSingleItem(state.inspectionTemplate, templateId, deleted),
      })),
    archiveTemplate: (templateId, projectId) => inspectionTemplateClient.remove({ templateId, projectId }),
    restoreTemplate: (templateId, projectId) => inspectionTemplateClient.restore({ templateId, projectId }),
    resetInspectionTemplates: () => set(getDefaultState()),
  };
};

export type { InspectionTemplatesSlice, InspectionTemplatesItems } from './types';
export {
  filterTemplatesByEntityState,
  sortTemplatesByModifiedAtDesc,
} from './model';
